import { Pipe, PipeTransform } from '@angular/core';
import { DateFormatLocalizationService } from 'src/app/core/services/date-format-localization.service';
import { TimezoneService } from 'src/app/core/utils/timezone.service';

@Pipe({
    name: 'localizedDate',
    pure: false,
})
export class LocalizedDatePipe implements PipeTransform {
    constructor(
        private readonly dateFormatService: DateFormatLocalizationService,
        private readonly timezoneService: TimezoneService,
    ) { }

    transform(
        value: Date | string | null | undefined,
        mode: 'date' | 'datetime' | 'date-short-year' | string = 'date',
        forceTimezoneConversion: boolean = false,
    ): string {
        if (value === null || value === undefined || value === '') return '';

        const config = this.dateFormatService.getCurrentConfig();
        const countryCode = this.dateFormatService.getCurrentCountryCode();

        const displayFormat = forceTimezoneConversion
            ? this.timezoneService.getDateFormatByCountry(countryCode)
            : config.displayFormat;
        const datePattern = mode === 'date-short-year'
            ? displayFormat.replace('yyyy', 'yy')
            : (mode === 'date' || mode === 'datetime' ? displayFormat : mode);

        const parsed = this.parseInput(value, countryCode, forceTimezoneConversion);
        if (!parsed || Number.isNaN(parsed.getTime())) return '';

        const datePart = this.applyDatePattern(parsed, datePattern);
        if (mode !== 'datetime') return datePart;

        const hh = String(parsed.getHours()).padStart(2, '0');
        const mm = String(parsed.getMinutes()).padStart(2, '0');
        return `${datePart} ${hh}:${mm}`;
    }

    private parseInput(value: Date | string, countryCode: string, forceTimezoneConversion = false): Date | null {
        if (value instanceof Date) return value;

        const raw = String(value).trim();
        if (!raw) return null;

        const normalized = raw.includes(' ') ? raw.replace(' ', 'T') : raw;
        const hasTimezone = /(?:Z|[+\-]\d{2}:\d{2})$/i.test(normalized);

        if (hasTimezone || forceTimezoneConversion) {
            return this.timezoneService.convertUtcToLocal(normalized, countryCode);
        }

        const [datePart = '', timePart = '00:00:00'] = normalized.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute, second = 0] = timePart.split(':').map(Number);

        if (!year || !month || !day) {
            const fallback = new Date(normalized);
            return Number.isNaN(fallback.getTime()) ? null : fallback;
        }

        return new Date(year, month - 1, day, hour || 0, minute || 0, second || 0);
    }

    private applyDatePattern(date: Date, pattern: string): string {
        const dd = String(date.getDate()).padStart(2, '0');
        const MM = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = String(date.getFullYear());
        const yy = yyyy.slice(-2);

        return pattern
            .replace('yyyy', yyyy)
            .replace('yy', yy)
            .replace('dd', dd)
            .replace('MM', MM);
    }
}
